import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthApiService } from '../core/auth/auth-api.service';
import { decodeJwtClaims, secondsUntilExpiry } from '../core/auth/jwt';
import * as AuthActions from '../store/auth/auth.actions';
import { selectAccessToken } from '../store/auth/auth.selectors';

const AUTH_PATH_MARKER = '/Auth/';
const PUBLIC_PATH_MARKER = '/Public/';

const PROTECTED_PREFIXES = ['/developer', '/admin', '/customer-dashboard', '/profile'];

// Some endpoints (e.g. Idempotency-Key protected writes) must NOT be retried automatically.
export const SKIP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);

function isAuthRequest(url: string): boolean {
    return url.includes(AUTH_PATH_MARKER);
}

function isPublicRequest(url: string): boolean {
    return url.includes(PUBLIC_PATH_MARKER);
}

function isProtectedRoute(url: string): boolean {
    const normalized = (url || '').toLowerCase();
    return PROTECTED_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(authApi: AuthApiService): Promise<string | null> {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            try {
                const session = await firstValueFrom(authApi.refresh());
                return session?.access_token ? String(session.access_token) : null;
            } catch {
                return null;
            } finally {
                refreshInFlight = null;
            }
        })();
    }
    return refreshInFlight;
}

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const store = inject(Store);
    const router = inject(Router);
    const authApi = inject(AuthApiService);

    // Never attach tokens or trigger refresh for Auth or Public endpoints.
    if (isAuthRequest(req.url) || isPublicRequest(req.url)) {
        return next(req);
    }

    return from(firstValueFrom(store.select(selectAccessToken))).pipe(
        switchMap((token) => {
            const rawToken = token ? String(token) : null;
            const claims = decodeJwtClaims(rawToken);
            const ttlSeconds = secondsUntilExpiry(claims);
            const isTokenStale = rawToken && (ttlSeconds === null || ttlSeconds <= 300);

            // For long-running requests (uploads, bulk operations), avoid sending tokens that are about to expire.
            // This prevents "jwt expired" during the request body upload and avoids unnecessary retries.
            // However, if the caller explicitly opted out of auth refresh (SKIP_AUTH_REFRESH), skip this too
            // so that critical idempotent flows are not disrupted by a failed pre-emptive refresh.
            const skipRefresh = req.context.get(SKIP_AUTH_REFRESH);
            if (isTokenStale && !skipRefresh) {
                return from(refreshAccessToken(authApi)).pipe(
                    switchMap((newToken) => {
                        if (!newToken) {
                            store.dispatch(AuthActions.clearAuthState());
                            const currentUrl = router.url || '/';
                            if (isProtectedRoute(currentUrl)) {
                                store.dispatch(AuthActions.sessionExpired({ returnUrl: currentUrl }));
                            }
                            const synthetic = new HttpErrorResponse({
                                status: 401,
                                statusText: 'Unauthorized',
                                url: req.url,
                                error: { error: { code: 'AUTH_INVALID_TOKEN', message: 'Session expired' } }
                            });
                            return throwError(() => synthetic);
                        }

                        store.dispatch(AuthActions.setAccessToken({ accessToken: newToken }));
                        const refreshedReq = req.clone({ setHeaders: { 'X-nibni-Authorization': `Bearer ${newToken}` } });
                        return next(refreshedReq);
                    })
                );
            }

            const authReq = rawToken
                ? req.clone({ setHeaders: { 'X-nibni-Authorization': `Bearer ${rawToken}` } })
                : req;

            return next(authReq).pipe(
                catchError((error) => {
                    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
                        return throwError(() => error);
                    }

                    const errorCode =
                        (error?.error as any)?.error?.code
                        || (error?.error as any)?.code
                        || null;

                    // Permissions were revoked/changed server-side → force a full re-login (do not refresh silently).
                    if (errorCode === 'AUTHZ_PERM_VER_MISMATCH') {
                        store.dispatch(AuthActions.signOut({ redirectTo: '/login' }));
                        return throwError(() => error);
                    }

                    // Avoid refresh loops (if Auth itself returns 401, just fail).
                    if (isAuthRequest(req.url)) {
                        store.dispatch(AuthActions.clearAuthState());
                        return throwError(() => error);
                    }

                    const skipRefreshFlag = req.context.get(SKIP_AUTH_REFRESH);

                    return from(refreshAccessToken(authApi)).pipe(
                        switchMap((newToken) => {
                            if (!newToken) {
                                // For SKIP_AUTH_REFRESH requests, do NOT clear auth state or show
                                // session-expired dialog — just throw the error so the component
                                // can show a user-friendly message. These are critical flows
                                // (OTP verify, lead/reservation creation) where losing the form
                                // state is worse than letting the component handle the error.
                                if (!skipRefreshFlag) {
                                    store.dispatch(AuthActions.clearAuthState());
                                    const currentUrl = router.url || '/';
                                    if (isProtectedRoute(currentUrl)) {
                                        store.dispatch(AuthActions.sessionExpired({ returnUrl: currentUrl }));
                                    }
                                }
                                return throwError(() => error);
                            }

                            store.dispatch(AuthActions.setAccessToken({ accessToken: newToken }));

                            // Always retry after a successful refresh. The write endpoints that
                            // previously opted out of retry via SKIP_AUTH_REFRESH all use
                            // Idempotency-Key headers, so the server handles duplicate detection.
                            const retry = req.clone({ setHeaders: { 'X-nibni-Authorization': `Bearer ${newToken}` } });
                            return next(retry);
                        })
                    );
                })
            );
        })
    );
};
